/**
 * Sign-up page. Email + name + password with live zxcvbn strength meter.
 *
 * On success, server sends a verify-email link; the user lands on
 * EmailSentPage.tsx telling them to check their inbox. The verify-email
 * link in the email points at the API host — API verifies, sets the
 * session cookie (autoSignInAfterVerification: true), then redirects
 * to /email-verified.
 */
import { useNavigate, Link, Navigate, useSearchParams } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { authClient } from '@/shared/auth/authClient';
import { useAuth } from '@/shared/auth/AuthProvider';
import { useSignupMode } from '@/shared/api/hooks';
import { Button, Form, FormTextField } from '@/interfaces/learner/ui';
import LoadingIcon from '@/foundation/ui/LoadingIcon';
import AuthLayout from '@/shared/auth/AuthLayout';
import Logomark from '@/shared/components/Logomark';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  name: z.string().min(1, 'Name is required').max(100),
  password: z.string().min(8, 'At least 8 characters').max(128, 'At most 128 characters'),
});
type Values = z.infer<typeof schema>;

export default function SignUpPage() {
  const navigate = useNavigate();
  const { isAuthed, loading } = useAuth();
  const { data: signupMode } = useSignupMode();
  const [searchParams] = useSearchParams();

  // Invite code arrives as `?invite=…` either directly (admin shared the
  // SPA URL with the code pre-attached) or via AcceptInvitationPage's
  // sign-up redirect (`?redirect=/accept-invitation?code=…` — see below
  // for how we recover the code from that nested form).
  const directInviteCode = searchParams.get('invite');
  // Mirror LoginPage's open-redirect guard: only honour same-origin paths
  // (start with a single forward slash). Today the redirect param is only
  // parsed for an invite code, not navigated to — but defence in depth
  // means the value never gets to influence anything if it's not safe.
  const rawRedirect = searchParams.get('redirect') ?? '';
  const redirectTarget =
    rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '';
  const redirectInviteCode =
    redirectTarget && redirectTarget.includes('accept-invitation')
      ? new URLSearchParams(redirectTarget.split('?')[1] ?? '').get('code')
      : null;
  const inviteCode = directInviteCode ?? redirectInviteCode;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', name: '', password: '' },
    mode: 'onBlur',
  });
  const passwordValue = form.watch('password');
  const emailValue = form.watch('email');
  const nameValue = form.watch('name');

  const onSubmit: SubmitHandler<Values> = async ({ email, password, name }) => {
    const { error } = await authClient.signUp.email({
      email,
      password,
      name,
      callbackURL: `${window.location.origin}/email-verified`,
      ...(inviteCode ? { inviteCode } : {}),
    } as Parameters<typeof authClient.signUp.email>[0]);

    if (error) {
      // The HIBP plugin's customPasswordCompromisedMessage flows through
      // here verbatim. Same for any other server-side rejection.
      toast.error(error.message ?? 'Sign-up failed');
      return;
    }

    navigate('/email-sent', { state: { email } });
  };

  if (!loading && isAuthed) {
    return <Navigate to="/" replace />;
  }

  // Avoid flashing the sign-up form while the initial auth check is in
  // flight (mirrors the same guard on LoginPage).
  if (loading) {
    return (
      <AuthLayout>
        <div />
      </AuthLayout>
    );
  }

  // Invite-only mode + no code in the URL → show a friendly landing
  // instead of letting the user fill the form and get rejected by the
  // server-side gate. We render this BEFORE the form so the user
  // doesn't see the inputs at all.
  if (signupMode?.inviteOnly && !inviteCode) {
    return (
      <AuthLayout>
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <Logomark size={48} />
          </div>
          <h1 className="text-2xl font-bold">Sign-up is invite-only</h1>
        </div>
        <div className="space-y-4 text-center">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Formency is currently invite-only. You&apos;ll need an invite link from someone
            who&apos;s already on the app to create an account.
          </p>
          <div className="text-sm pt-2">
            <Link to="/login" className="text-[var(--color-accent-text)] hover:underline">
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const submitting = form.formState.isSubmitting;

  return (
    <AuthLayout>
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <Logomark size={48} />
        </div>
        <h1 className="text-2xl font-bold">Create an account</h1>
        {inviteCode && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
            style={{
              background: 'var(--color-accent-subtle)',
              color: 'var(--color-accent-text)',
            }}
          >
            <Mail size={11} strokeWidth={2.5} />
            Redeeming invite
          </div>
        )}
      </div>
      <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormTextField
          control={form.control}
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          isRequired
          placeholder="you@example.com"
        />
        <FormTextField
          control={form.control}
          name="name"
          label="Name"
          type="text"
          autoComplete="name"
          isRequired
          placeholder="Your name"
        />
        <FormTextField
          control={form.control}
          name="password"
          label="Password"
          type="password"
          autoComplete="new-password"
          isRequired
          placeholder="At least 8 characters"
        />
        <PasswordStrengthMeter
          password={passwordValue ?? ''}
          userInputs={[emailValue, nameValue].filter(Boolean) as string[]}
        />
        <Button type="submit" isDisabled={submitting} className="w-full">
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingIcon size="sm" />
              Creating account...
            </span>
          ) : (
            'Create account'
          )}
        </Button>
        <div className="text-center text-sm pt-2">
          Already have an account?{' '}
          <Link to="/login" className="text-[var(--color-accent-text)] hover:underline">
            Sign in
          </Link>
        </div>
      </Form>
    </AuthLayout>
  );
}
